// Skerpa Agent Configuration — Settings panel
// Hermes gateway + API server connection settings

import { useState, useCallback, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Bot, Save, RefreshCw, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { useSkerpaStore } from '@/stores/skerpaStore'

export function SkerpaAgentConfig() {
  const {
    gatewayUrl, apiUrl, enabled,
    gatewayAvailable, apiAvailable,
    setGatewayUrl, setApiUrl, setEnabled, setStatus,
  } = useSkerpaStore()

  const [tempGateway, setTempGateway] = useState(gatewayUrl)
  const [tempApi, setTempApi] = useState(apiUrl)
  const [testing, setTesting] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Check connection on mount
  useEffect(() => { testConnection() }, [])

  const testConnection = useCallback(async () => {
    setTesting(true)
    let gwOk = false
    let apiOk = false

    try {
      const res = await fetch('/api/hermes/health', { signal: AbortSignal.timeout(5000) })
      if (res.ok) {
        const data = await res.json()
        gwOk = data.available === true
      }
    } catch { /* offline */ }

    try {
      const res = await fetch('/api/skerpa/health', { signal: AbortSignal.timeout(5000) })
      if (res.ok) {
        const data = await res.json()
        apiOk = data.available === true
      }
    } catch { /* offline */ }

    setStatus(gwOk, apiOk)
    setTesting(false)
  }, [setStatus])

  const handleSave = () => {
    setGatewayUrl(tempGateway.trim())
    setApiUrl(tempApi.trim())
    setDirty(false)
    testConnection()
  }

  const StatusDot = ({ ok }: { ok: boolean }) => (
    ok
      ? <CheckCircle className="w-4 h-4 text-green-600" />
      : <XCircle className="w-4 h-4 text-warm-400" />
  )

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-business/10 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-business" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base font-semibold">Skerpa Agent</CardTitle>
                <CardDescription className="text-text-muted">Hermes-powered AI copilot</CardDescription>
              </div>
            </div>
            {enabled ? (
              <Badge className="bg-business/10 text-business border border-business/20 flex-shrink-0">Enabled</Badge>
            ) : (
              <Badge variant="outline" className="flex-shrink-0">Disabled</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-text">Enable Skerpa</p>
              <p className="text-xs text-text-muted">Show the Skerpa copilot button in the header</p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-business' : 'bg-warm-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Connection Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-text">Connection Status</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={testConnection}
                disabled={testing}
                className="h-7 text-xs"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Test
              </Button>
            </div>
            <div className="space-y-1.5 px-1">
              <div className="flex items-center gap-2 text-sm">
                <StatusDot ok={gatewayAvailable} />
                <span className="text-text-secondary">Webhook Gateway</span>
                <span className="text-xs text-text-muted font-mono ml-auto">{gatewayUrl}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <StatusDot ok={apiAvailable} />
                <span className="text-text-secondary">Chat API Server</span>
                <span className="text-xs text-text-muted font-mono ml-auto">{apiUrl}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold">Connection Settings</CardTitle>
          <CardDescription className="text-text-muted">
            Configure the Hermes gateway and API server URLs. These are set server-side in .env but shown here for reference.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Webhook Gateway URL</label>
            <input
              type="text"
              value={tempGateway}
              onChange={(e) => { setTempGateway(e.target.value); setDirty(true) }}
              placeholder="http://localhost:8644"
              className="w-full px-3 py-2 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-business/30 font-mono text-text-secondary"
            />
            <p className="text-xs text-text-muted mt-1">Receives agent trigger requests (competitor monitor, market research, etc.)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Chat API Server URL</label>
            <input
              type="text"
              value={tempApi}
              onChange={(e) => { setTempApi(e.target.value); setDirty(true) }}
              placeholder="http://localhost:8642"
              className="w-full px-3 py-2 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-business/30 font-mono text-text-secondary"
            />
            <p className="text-xs text-text-muted mt-1">OpenAI-compatible endpoint for conversational chat (Skerpa copilot)</p>
          </div>

          {dirty && (
            <Button onClick={handleSave} className="bg-business hover:opacity-90 text-white">
              <Save className="w-4 h-4 mr-1" />
              Save & Test
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-secondary leading-relaxed">
            Skerpa uses the Hermes Agent framework for AI-powered copilot capabilities.
            The webhook gateway handles async agent tasks (competitor monitoring, market research).
            The chat API server provides real-time conversational assistance with access to
            PM knowledge, web search, and your project context.
          </p>
          <div className="mt-3 text-xs text-text-muted space-y-1">
            <p>Gateway config: <code className="font-mono bg-warm-100 px-1 rounded">~/.hermes/config.yaml</code></p>
            <p>API env: <code className="font-mono bg-warm-100 px-1 rounded">apps/api/.env</code> (HERMES_* variables)</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
